import axios from 'axios';

// Base URL da API
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'development' 
    ? 'http://localhost:8000'
    : 'https://inovtraapi.onrender.com');

/**
 * Função auxiliar para lidar com as respostas da API e erros.
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    // Tenta ler o corpo do erro como JSON, senão usa o texto de status
    const errorData = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(errorData.detail || 'Ocorreu um erro desconhecido na API.');
  }

  // Verifica se a URL da resposta é de um endpoint de download
  const isDownloadEndpoint = response.url.includes('/excel') || 
                             response.url.includes('/reports/xtr-formatted') ||
                             response.url.includes('/download-xte') ||
                             response.url.includes('/download-xml') ||
                             response.url.includes('/template') ||
                             response.url.includes('/csv');

  if (isDownloadEndpoint) {
    return response.blob(); // Retorna o arquivo como Blob
  }

  return response.json(); // Retorna os dados como JSON para todos os outros endpoints
};

/**
 * Realiza uma chamada à API com autenticação e FormData.
 */
const apiFetch = (endpoint, formData, token) => {
  return fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  }).then(handleResponse);
};

/**
 * Realiza uma chamada à API com autenticação e JSON.
 */
const apiJsonFetch = (endpoint, data, token, method = 'POST') => {
  return fetch(`${API_BASE_URL}${endpoint}`, {
    method: method,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  }).then(handleResponse);
};


/**
 * Valida os arquivos TISS no back-end.
 * @param {File[]} files - Array de arquivos a serem validados.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<object>} - Resultado da validação.
 */
export const validateFilesAPI = (files, token) => {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  return apiFetch('/validate-files', formData, token);
};

/**
 * Analisa os arquivos XTE e XTR e retorna o resultado em JSON.
 * @param {File[]} xteFiles - Arquivos de envio.
 * @param {File[]} xtrFiles - Arquivos de retorno.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<object>} - Resultado da análise.
 */
export const analyzeFilesAPI = (xteFiles, xtrFiles, token) => {
  const formData = new FormData();
  xteFiles.forEach(file => formData.append('xte_files', file));
  xtrFiles.forEach(file => formData.append('xtr_files', file));
  return apiFetch('/analyze', formData, token);
};

/**
 * Baixa o relatório completo da análise em formato Excel.
 * @param {File[]} xteFiles - Arquivos de envio.
 * @param {File[]} xtrFiles - Arquivos de retorno.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<Blob>} - O arquivo Excel como um Blob.
 */
export const downloadAnalysisExcelAPI = (xteFiles, xtrFiles, token) => {
  const formData = new FormData();
  xteFiles.forEach(file => formData.append('xte_files', file));
  xtrFiles.forEach(file => formData.append('xtr_files', file));
  return apiFetch('/analyze/excel', formData, token);
};

/**
 * Baixa o relatório formatado apenas dos arquivos XTR em Excel.
 * @param {File[]} xtrFiles - Arquivos de retorno.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<Blob>} - O arquivo Excel como um Blob.
 */
export const downloadXtrReportAPI = (xtrFiles, token) => {
  const formData = new FormData();
  xtrFiles.forEach(file => formData.append('xtr_files', file));
  return apiFetch('/reports/xtr-formatted', formData, token);
};


/**
 * Busca informações de uma empresa pelo CNPJ.
 * @param {string} cnpj - CNPJ da empresa (apenas números).
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<object>} - Informações da empresa.
 */
export const getCompanyByCNPJAPI = (cnpj, token) => {
  return apiJsonFetch('/companies/search', { cnpj }, token);
};

/**
 * Cria uma nova empresa no sistema.
 * @param {object} companyData - Dados da empresa.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<object>} - Empresa criada.
 */
export const createCompanyAPI = (companyData, token) => {
  return apiJsonFetch('/companies', companyData, token);
};

/**
 * Atualiza o perfil do usuário com informações adicionais.
 * @param {object} profileData - Dados do perfil do usuário.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<object>} - Perfil atualizado.
 */
export const updateUserProfileAPI = (profileData, token) => {
  return apiJsonFetch('/user/profile', profileData, token, 'PUT');
};

/**
 * Busca o perfil completo do usuário incluindo dados da empresa.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<object>} - Perfil completo do usuário.
 */
export const getUserProfileAPI = (token) => {
  return fetch(`${API_BASE_URL}/user/profile`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  }).then(handleResponse);
};


/**
 * Consolida múltiplos arquivos XTE em dados JSON.
 * @param {File[]} xteFiles - Arquivos XTE para consolidação.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<object>} - Dados consolidados.
 */
export const consolidateXteFilesAPI = (xteFiles, token) => {
  const formData = new FormData();
  xteFiles.forEach(file => formData.append('xte_files', file));
  return apiFetch('/consolidate/xte', formData, token);
};

/**
 * Consolida múltiplos arquivos XTE e baixa como Excel.
 * @param {File[]} xteFiles - Arquivos XTE para consolidação.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<Blob>} - Arquivo Excel como Blob.
 */
export const consolidateXteFilesExcelAPI = (xteFiles, token) => {
  const formData = new FormData();
  xteFiles.forEach(file => formData.append('xte_files', file));
  return apiFetch('/consolidate/xte/excel', formData, token);
};

/**
 * Consolida múltiplos arquivos XTE e baixa como CSV.
 * @param {File[]} xteFiles - Arquivos XTE para consolidação.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<Blob>} - Arquivo CSV como Blob.
 */
export const consolidateXteFilesCsvAPI = (xteFiles, token) => {
  const formData = new FormData();
  xteFiles.forEach(file => formData.append('xte_files', file));
  return apiFetch('/consolidate/xte/csv', formData, token);
};


/**
 * Gera arquivos XTE/XML a partir de Excel e retorna informações sobre os arquivos gerados.
 * @param {File} excelFile - Arquivo Excel com dados.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<object>} - Informações sobre os arquivos gerados.
 */
export const generateXteFromExcelAPI = (excelFile, token) => {
  const formData = new FormData();
  formData.append('excel_file', excelFile);
  return apiFetch('/generate/xte', formData, token);
};

/**
 * Gera e baixa arquivos XTE como ZIP.
 * @param {File} excelFile - Arquivo Excel com dados.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<Blob>} - Arquivo ZIP como Blob.
 */
export const generateAndDownloadXteZipAPI = (excelFile, token) => {
  const formData = new FormData();
  formData.append('excel_file', excelFile);
  return apiFetch('/generate/xte/download-xte', formData, token);
};

/**
 * Gera e baixa arquivos XML como ZIP.
 * @param {File} excelFile - Arquivo Excel com dados.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<Blob>} - Arquivo ZIP como Blob.
 */
export const generateAndDownloadXmlZipAPI = (excelFile, token) => {
  const formData = new FormData();
  formData.append('excel_file', excelFile);
  return apiFetch('/generate/xte/download-xml', formData, token);
};

/**
 * Baixa o template Excel para geração de arquivos XTE.
 * @param {string} token - Token de autenticação JWT.
 * @returns {Promise<Blob>} - Arquivo template como Blob.
 */
export const downloadExcelTemplateAPI = (token) => {
  return fetch(`${API_BASE_URL}/generate/template`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  }).then(handleResponse);
};